"use client";

import {
  TextField,
  Button,
  Typography,
  Box,
  Paper,
  Grid,
  Divider,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useState } from "react";
import { useSelector } from "react-redux";
import { axiosRequest } from "../../utility/axios";
import { getDate } from "../../helper";
import WithdrawDialog from "./WithdrawDialog";

export default function Withdraw() {
  const loginSlice = useSelector((state) => state.loginSlice);
  const token = loginSlice.token;

  const [searchId, setSearchId]   = useState("");
  const [users, setUsers]         = useState([]);
  const [userKyc, setUserKyc]     = useState({});
  const [tableJoin, setTableJoin] = useState({});
  const [lastDeposit, setLastDeposit] = useState({});
  const [selectedUser, setSelectedUser] = useState(null);
  const [dialogOpen, setDialogOpen]     = useState(false);

  const handleSearch = async () => {
    const input = searchId.trim();
    if (!input) return;
    let param;
    if (/^\d{10}$/.test(input)) param = `phone=${input}`;
    else if (/^[a-zA-Z\s]+$/.test(input)) param = `name=${encodeURIComponent(input)}`;
    else param = `playerId=${input}`;
    const res = await axiosRequest("GET", `/user_details?${param}`, token);
    setUsers(res?.data?.userDetailsList || (res?.data?.userDetails ? [res.data.userDetails] : []));
    setUserKyc(res.data?.kycDetails || null);
    setTableJoin(res.data?.userTableJoin || null);
    setLastDeposit(res.data?.userLastDeposit || null);
  };

  const handleOpenWithdraw = (user) => {
    setSelectedUser(user);
    setDialogOpen(true);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, mt: { xs: 7, md: 12 }, pb: 8 }}>
      <Typography variant="h4" fontWeight={600} mb={3}>
        Withdraw Management
      </Typography>

      {/* Search */}
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Phone / Player ID / Name"
              value={searchId}
              onChange={(e) => setSearchId(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Button variant="contained" sx={{ height: 56 }} onClick={handleSearch}>
              Search User
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* User result */}
      {users.length > 0 && (
        <Paper elevation={4} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" mb={2}>Search Result</Typography>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Player ID</TableCell>
                <TableCell>Deposit Wallet</TableCell>
                <TableCell>Winning Balance</TableCell>
                <TableCell>Promo Balance</TableCell>
                <TableCell>Outstanding Loan</TableCell>
                <TableCell sx={{ fontWeight: 700, color: "success.main" }}>Withdrawable</TableCell>
                <TableCell align="center">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => {
                const wb = Number(user.winning_balance || 0);
                const dep = Number(user.cash_deposite || 0);
                const loan = Number(user.loan_distributed || 0);
                const net = Math.max(0, wb + dep - loan);
                return (
                  <TableRow key={user?._id}>
                    <TableCell>
                      <Typography fontWeight={600}>{user?.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{user?.playerId}</Typography>
                    </TableCell>
                    <TableCell>{user?.playerId}</TableCell>
                    <TableCell>{Number(user.cash_deposite || 0).toLocaleString("en-IN")}</TableCell>
                    <TableCell>{wb.toLocaleString("en-IN")}</TableCell>
                    <TableCell>{Number(user.promotional_balance || 0).toLocaleString("en-IN")}</TableCell>
                    <TableCell>
                      {loan > 0 ? (
                        <Typography color="error.main" fontWeight={600}>
                          {loan.toLocaleString("en-IN")}
                        </Typography>
                      ) : (
                        <Typography color="text.disabled">—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight={700} color="success.main" variant="h6">
                        {net.toLocaleString("en-IN")}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton color="primary" onClick={() => handleOpenWithdraw(user)}>
                        <AddIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* KYC */}
      {userKyc && users.length > 0 && (
        <Paper elevation={3} sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" mb={2}>KYC Details</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Typography>Aadhaar Verified</Typography>
              <Chip
                label={userKyc?.is_aadhar_verified ? "VERIFIED" : "NOT VERIFIED"}
                color={userKyc?.is_aadhar_verified ? "success" : "error"}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography>PAN Verified</Typography>
              <Chip
                label={userKyc?.is_pan_verified ? "VERIFIED" : "NOT VERIFIED"}
                color={userKyc?.is_pan_verified ? "success" : "error"}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography>Bank Verified</Typography>
              <Chip
                label={userKyc?.is_bank_verified ? "VERIFIED" : "NOT VERIFIED"}
                color={userKyc?.is_bank_verified ? "success" : "error"}
              />
            </Grid>
          </Grid>
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography>Aadhaar Name</Typography>
              <Typography fontWeight={600}>{userKyc?.aadhar_details?.name}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography>PAN Name</Typography>
              <Typography fontWeight={600}>{userKyc?.pen_details?.pan_name}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography>Aadhaar Number</Typography>
              <Typography fontWeight={600}>
                {userKyc?.aadhar_details?.uid?.replace(/.(?=.{4})/g, "X")}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography>PAN Number</Typography>
              <Typography fontWeight={600}>{userKyc?.pen_details?.pan_card}</Typography>
            </Grid>
          </Grid>

          {userKyc?.is_bank_verified && userKyc?.bank_details?.account_number && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" color="success.dark" fontWeight={700} mb={1.5}>
                ✓ Verified Bank Account
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography>Account Holder</Typography>
                  <Typography fontWeight={600}>{userKyc.bank_details.account_holder_name || "—"}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography>Account Number</Typography>
                  <Typography fontWeight={600}>{userKyc.bank_details.account_number}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography>IFSC Code</Typography>
                  <Typography fontWeight={600}>{userKyc.bank_details.ifsc_code || "—"}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography>Bank Name</Typography>
                  <Typography fontWeight={600}>{userKyc.bank_details.bank_name || "—"}</Typography>
                </Grid>
                {userKyc.bank_details.branch && (
                  <Grid item xs={12} md={4}>
                    <Typography>Branch</Typography>
                    <Typography fontWeight={600}>{userKyc.bank_details.branch}</Typography>
                  </Grid>
                )}
              </Grid>
            </>
          )}
        </Paper>
      )}

      {tableJoin && users.length > 0 && (
        <Paper elevation={3} sx={{ p: 3, mt: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography>Table Joining Time</Typography>
              <Typography fontWeight={600}>{getDate(tableJoin?.process_time)}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography>Last Deposit</Typography>
              <Typography fontWeight={600}>{lastDeposit?.txn_amount}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography>Last Cashback</Typography>
              <Typography fontWeight={600}>{users?.[0]?.last_cb}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography>Call Time</Typography>
              <Typography fontWeight={600}>{getDate(tableJoin?.call_date_time)}</Typography>
            </Grid>
          </Grid>
        </Paper>
      )}

      <WithdrawDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        initialUser={selectedUser}
        initialJoinData={tableJoin}
      />

    </Box>
  );
}
